import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

/**
 * Rich inline editor for zone text templates. Tokens like {{title}} appear as
 * visual pill chips that are deleted as a unit; the serialized output still
 * uses the `{{token}}` syntax the backend substitution layer expects.
 *
 * Storage contract: identical to a plain string input — `value` and
 * `onChange(text)` hold and emit the serialized template. No new data shape.
 */

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Editor = styled.div`
  min-height: 3.2rem;
  border: var(--hm-border);
  border-radius: var(--hm-radius);
  background: #fafafa;
  padding: 0.5rem 0.6rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
  line-height: 1.6;
  outline: none;
  white-space: pre-wrap;
  word-break: break-word;

  &:focus {
    background: #ffffff;
  }

  .tp-pill {
    display: inline-block;
    padding: 0.05rem 0.35rem;
    margin: 0 0.15rem;
    background: #ffefb0;
    border: 1px dashed #a87400;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
    color: #6b4200;
    user-select: none;
    vertical-align: baseline;
  }
  .tp-pill::before {
    content: "{{ ";
    opacity: 0.55;
  }
  .tp-pill::after {
    content: " }}";
    opacity: 0.55;
  }
`;

const AddRow = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: center;
`;

const AddBtn = styled.button`
  background: #fff;
  border: var(--hm-border);
  border-radius: var(--hm-radius);
  padding: 0.25rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: var(--hm-shadow-sm);
  font-family: inherit;

  &:active:not(:disabled) {
    transform: var(--hm-press-translate);
    box-shadow: var(--hm-shadow-sm-pressed);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddSelect = styled.select`
  border: var(--hm-border);
  border-radius: var(--hm-radius);
  padding: 0.25rem 0.35rem;
  font-family: inherit;
  font-size: 0.75rem;
  background: #fff;
`;

const TOKEN_RE = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;

interface Props {
  value: string;
  onChange: (next: string) => void;
  allowedTokens: ReadonlyArray<string>;
  sampleOf?: (token: string) => string;
  placeholder?: string;
}

/** DOM → template string. Pill spans (data-token) round-trip to {{token}}. */
function serializeNode(root: HTMLElement): string {
  let out = "";
  const walk = (n: Node): void => {
    if (n.nodeType === Node.TEXT_NODE) {
      out += n.textContent ?? "";
      return;
    }
    if (n.nodeType === Node.ELEMENT_NODE) {
      const el = n as HTMLElement;
      if (el.dataset && el.dataset.token) {
        out += `{{${el.dataset.token}}}`;
        return;
      }
      if (el.tagName === "BR") {
        out += "\n";
        return;
      }
      // strip other wrappers — just recurse into children
      for (let i = 0; i < el.childNodes.length; i++) walk(el.childNodes[i]);
    }
  };
  for (let i = 0; i < root.childNodes.length; i++) walk(root.childNodes[i]);
  return out;
}

/** Template string → editor HTML (pills interspersed with text). */
function renderToHtml(value: string): string {
  const escapeHtml = (s: string): string =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let html = "";
  let idx = 0;
  let m: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(value)) !== null) {
    html += escapeHtml(value.slice(idx, m.index));
    const token = m[1];
    html += `<span class="tp-pill" data-token="${escapeHtml(token)}" contenteditable="false">${escapeHtml(token)}</span>`;
    idx = m.index + m[0].length;
  }
  html += escapeHtml(value.slice(idx));
  return html;
}

export const TokenPillEditor: React.FC<Props> = ({
  value,
  onChange,
  allowedTokens,
  sampleOf,
  placeholder,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [nextToken, setNextToken] = useState<string>(allowedTokens[0] ?? "");

  // Sync external value -> DOM, but only when the external value diverges
  // from what's currently in the editor (so we don't clobber cursor while
  // typing). Mount covers the initial render too.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const current = serializeNode(el);
    if (current !== value) {
      el.innerHTML = renderToHtml(value);
    }
  }, [value]);

  useEffect(() => {
    if (allowedTokens.length > 0 && !allowedTokens.includes(nextToken as string)) {
      setNextToken(allowedTokens[0]);
    }
  }, [allowedTokens, nextToken]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const serialized = serializeNode(el);
    if (serialized !== value) onChange(serialized);
  }, [onChange, value]);

  const insertPill = useCallback(
    (token: string) => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      const sel = window.getSelection();
      const span = document.createElement("span");
      span.className = "tp-pill";
      span.setAttribute("data-token", token);
      span.setAttribute("contenteditable", "false");
      span.textContent = token;
      if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(span);
        // move caret to after the inserted pill
        const afterSpace = document.createTextNode(" ");
        span.parentNode?.insertBefore(afterSpace, span.nextSibling);
        const newRange = document.createRange();
        newRange.setStartAfter(afterSpace);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      } else {
        el.appendChild(span);
        el.appendChild(document.createTextNode(" "));
      }
      emit();
    },
    [emit]
  );

  return (
    <Shell>
      <Editor
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-label="text template"
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder}
      />
      {allowedTokens.length > 0 && (
        <AddRow>
          <AddSelect
            value={nextToken}
            onChange={(e) => setNextToken(e.target.value)}
            aria-label="token to insert"
          >
            {allowedTokens.map((t) => (
              <option key={t} value={t}>
                {t}
                {sampleOf ? ` — ${sampleOf(t)}` : ""}
              </option>
            ))}
          </AddSelect>
          <AddBtn
            type="button"
            disabled={!nextToken}
            onClick={() => nextToken && insertPill(nextToken)}
          >
            + insert token
          </AddBtn>
        </AddRow>
      )}
    </Shell>
  );
};

export default TokenPillEditor;
